import type {
  Highlighter,
  KeyAndParams,
  KeyPosition,
  NsFallback,
  ObserverMiddleware,
  ObserverRunProps,
  TolgeePlugin,
  Unwrapped,
  WrapperWrapProps,
} from '@tolgee/core';

// ============================================================================
// 不可见字符编码工具
// 使用零宽字符将 key 信息编码到翻译文本末尾
// ============================================================================

/** 零宽字符：用于编码二进制 0 和 1 */
const ZERO_WIDTH = {
  ZERO: '\u200B', // 零宽空格 (ZWSP) - 代表 0
  ONE: '\u200C', // 零宽非连接符 (ZWNJ) - 代表 1
  START: '\u200D', // 零宽连接符 (ZWJ) - 标记开始
  END: '\uFEFF', // 零宽非断空格 (BOM) - 标记结束
};

/** 检测文本中是否包含不可见标记 */
const containsInvisibleMark = (text: string): boolean => {
  return text.includes(ZERO_WIDTH.START) && text.includes(ZERO_WIDTH.END);
};

/** 将字符串编码为不可见字符序列 */
const encodeToInvisible = (str: string): string => {
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  for (const byte of bytes) {
    binary += byte.toString(2).padStart(8, '0');
  }
  let result = ZERO_WIDTH.START;
  for (const bit of binary) {
    result += bit === '0' ? ZERO_WIDTH.ZERO : ZERO_WIDTH.ONE;
  }
  result += ZERO_WIDTH.END;
  return result;
};

/** 从不可见字符序列解码字符串 */
const decodeFromInvisible = (encoded: string): string | null => {
  const startIdx = encoded.indexOf(ZERO_WIDTH.START);
  const endIdx = encoded.indexOf(ZERO_WIDTH.END);
  if (startIdx === -1 || endIdx === -1 || startIdx >= endIdx) {
    return null;
  }

  const invisiblePart = encoded.slice(startIdx + 1, endIdx);
  let binary = '';
  for (const char of invisiblePart) {
    if (char === ZERO_WIDTH.ZERO) binary += '0';
    else if (char === ZERO_WIDTH.ONE) binary += '1';
  }

  if (binary.length % 8 !== 0) return null;

  const bytes = new Uint8Array(binary.length / 8);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(binary.slice(i * 8, (i + 1) * 8), 2);
  }
  return new TextDecoder().decode(bytes);
};

/** 移除文本中的不可见标记 */
const removeInvisibleMark = (text: string): string => {
  const startIdx = text.indexOf(ZERO_WIDTH.START);
  const endIdx = text.indexOf(ZERO_WIDTH.END);
  if (startIdx === -1 || endIdx === -1) return text;
  return text.slice(0, startIdx) + text.slice(endIdx + 1);
};

// ============================================================================
// 类型定义
// ============================================================================

/** i18n 调用记录 */
export interface I18nRecord {
  key: string;
  text: string;
  ns?: string;
  defaultValue?: string;
  timestamp: number;
}

/** i18n 收集器接口，暴露到 window 供 Playwright 访问 */
export interface I18nCollector {
  records: Map<string, I18nRecord>;
  /** 获取当前页面所有 i18n 记录 */
  getAll(): I18nRecord[];
  /** 根据 key 前缀筛选记录 */
  getByPrefix(prefix: string): I18nRecord[];
  /** 清空记录（页面切换时调用） */
  clear(): void;
  /** 获取当前记录数量 */
  size(): number;
  /** 导出为 JSON（便于调试） */
  toJSON(): Record<string, string>;
}

declare global {
  interface Window {
    __i18n_collector__?: I18nCollector;
  }
}

// ============================================================================
// DOM 属性名常量
// ============================================================================
const DATA_I18N_KEY = 'data-i18n-key';
const DATA_I18N_NS = 'data-i18n-ns';
const OBSERVED_ATTRS = ['title', 'aria-label', 'placeholder'] as const;

// ============================================================================
// Observer 实现
// ============================================================================

/**
 * 创建 i18n 收集器 Observer
 *
 * 功能：
 * 1. wrap 时在翻译文本末尾添加不可见字符编码的 key 信息
 * 2. MutationObserver 监听 DOM 变化，检测带标记的文本节点
 * 3. 向父元素添加 data-i18n-key 属性，并移除不可见字符
 */
function createCollectorObserver(): ObserverMiddleware {
  return () => {
    let isRunning = false;
    let mutationObserver: MutationObserver | null = null;
    let targetElement: Node = document.body;

    /** 处理已标记的 key 元素映射（用于 findPositions） */
    const markedElements = new Map<string, Set<Element>>();

    /** 处理文本节点，提取 key 并添加 data 属性 */
    const processTextNode = (textNode: Text): void => {
      const text = textNode.textContent;
      if (!text || !containsInvisibleMark(text)) return;

      const encoded = decodeFromInvisible(text);
      if (!encoded) return;

      try {
        const data = JSON.parse(encoded) as { k: string; n?: string };
        const key = data.k;
        const ns = data.n;

        // 找到合适的父元素（跳过纯文本容器）
        let parent = textNode.parentElement;
        while (parent && parent.childNodes.length === 1 && parent.parentElement) {
          const tagName = parent.tagName.toLowerCase();
          // 对于语义化标签，直接使用
          if (
            [
              'span',
              'p',
              'div',
              'h1',
              'h2',
              'h3',
              'h4',
              'h5',
              'h6',
              'label',
              'button',
              'a',
              'li',
              'td',
              'th',
            ].includes(tagName)
          ) {
            break;
          }
          parent = parent.parentElement;
        }

        if (parent) {
          // 添加 data 属性
          const existingKeys = parent.getAttribute(DATA_I18N_KEY);
          if (existingKeys) {
            // 同一元素可能包含多个 key（如拼接的文本）
            if (!existingKeys.split(',').includes(key)) {
              parent.setAttribute(DATA_I18N_KEY, `${existingKeys},${key}`);
            }
          } else {
            parent.setAttribute(DATA_I18N_KEY, key);
          }

          if (ns) {
            parent.setAttribute(DATA_I18N_NS, ns);
          }

          // 记录元素映射
          if (!markedElements.has(key)) {
            markedElements.set(key, new Set());
          }
          markedElements.get(key)!.add(parent);
        }

        // 移除不可见字符，恢复原始文本
        textNode.textContent = removeInvisibleMark(text);
      } catch {
        // JSON 解析失败，忽略
      }
    };

    /** 清理属性中的不可见标记，避免污染 title/aria-label 等属性 */
    const processElementAttributes = (element: Element): void => {
      for (const attr of OBSERVED_ATTRS) {
        const value = element.getAttribute(attr);
        if (!value || !containsInvisibleMark(value)) continue;

        element.setAttribute(attr, removeInvisibleMark(value));
      }
    };

    /** 递归处理节点树 */
    const processNode = (node: Node): void => {
      if (node.nodeType === Node.TEXT_NODE) {
        processTextNode(node as Text);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        // 跳过 script、style 等不需要处理的元素
        const tagName = element.tagName.toLowerCase();
        if (['script', 'style', 'noscript', 'template'].includes(tagName)) {
          return;
        }
        processElementAttributes(element);
        for (const child of element.childNodes) {
          processNode(child);
        }
      }
    };

    /** MutationObserver 回调 */
    const handleMutations = (mutations: MutationRecord[]): void => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          for (const node of mutation.addedNodes) {
            processNode(node);
          }
        } else if (mutation.type === 'attributes') {
          const element = mutation.target;
          if (element.nodeType === Node.ELEMENT_NODE) {
            processElementAttributes(element as Element);
          }
        } else if (mutation.type === 'characterData') {
          const textNode = mutation.target;
          if (textNode.nodeType === Node.TEXT_NODE) {
            processTextNode(textNode as Text);
          }
        }
      }
    };

    return Object.freeze({
      /**
       * 包装翻译文本
       * 在文本末尾添加不可见字符编码的 key 信息
       */
      wrap(props: WrapperWrapProps): string {
        const { key, translation, ns } = props;
        const text = translation ?? '';

        // 仅在运行时添加不可见标记
        if (!isRunning) {
          return text;
        }

        // 编码 key 和 namespace
        const namespace = Array.isArray(ns) ? ns[0] : ns;
        const data = JSON.stringify({ k: key, n: namespace || undefined });
        const invisibleMark = encodeToInvisible(data);

        return text + invisibleMark;
      },

      /**
       * 解包文本，提取 key 信息
       */
      unwrap(text: string): Unwrapped {
        if (!containsInvisibleMark(text)) {
          return { text, keys: [] };
        }

        const encoded = decodeFromInvisible(text);
        const cleanText = removeInvisibleMark(text);

        if (!encoded) {
          return { text: cleanText, keys: [] };
        }

        try {
          const data = JSON.parse(encoded) as { k: string; n?: string };
          return {
            text: cleanText,
            keys: [{ key: data.k, ns: data.n }] as KeyAndParams[],
          };
        } catch {
          return { text: cleanText, keys: [] };
        }
      },

      /**
       * 启动观察器
       */
      run(props: ObserverRunProps): void {
        if (isRunning) return;
        isRunning = true;

        targetElement = props.options.targetElement ?? document.body;

        // 创建 MutationObserver
        mutationObserver = new MutationObserver(handleMutations);
        mutationObserver.observe(targetElement, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: [...OBSERVED_ATTRS],
        });

        // 处理已存在的 DOM
        processNode(targetElement);

        console.log('[I18nCollector] Observer 已启动，监听目标:', targetElement);
      },

      /**
       * 停止观察器
       */
      stop(): void {
        if (!isRunning) return;
        isRunning = false;

        if (mutationObserver) {
          mutationObserver.disconnect();
          mutationObserver = null;
        }

        markedElements.clear();
        console.log('[I18nCollector] Observer 已停止');
      },

      /**
       * 重新翻译页面元素
       */
      retranslate(): void {
        // 重新处理整个 DOM 树
        if (isRunning) {
          processNode(targetElement);
        }
      },

      /**
       * 高亮指定 key 的元素
       */
      highlight(key?: string, _ns?: NsFallback): Highlighter {
        if (!key) {
          return { unhighlight: () => {} };
        }

        const elements = markedElements.get(key);
        if (!elements || elements.size === 0) {
          return { unhighlight: () => {} };
        }

        // 添加高亮样式
        const originalStyles = new Map<Element, string>();
        for (const el of elements) {
          const htmlEl = el as HTMLElement;
          originalStyles.set(el, htmlEl.style.outline);
          htmlEl.style.outline = '2px solid #ff0000';
        }

        return {
          unhighlight() {
            for (const [el, style] of originalStyles) {
              (el as HTMLElement).style.outline = style;
            }
          },
        };
      },

      /**
       * 查找指定 key 的元素位置
       */
      findPositions(key?: string, _ns?: NsFallback): KeyPosition[] {
        if (!key) {
          // 返回所有 key 的位置
          const positions: KeyPosition[] = [];
          for (const [k, elements] of markedElements) {
            for (const el of elements) {
              const rect = el.getBoundingClientRect();
              positions.push({
                position: {
                  x: rect.left,
                  y: rect.top,
                  width: rect.width,
                  height: rect.height,
                },
                keyName: k,
                keyNamespace: el.getAttribute(DATA_I18N_NS) ?? '',
              });
            }
          }
          return positions;
        }

        const elements = markedElements.get(key);
        if (!elements) return [];

        return Array.from(elements).map((el) => {
          const rect = el.getBoundingClientRect();
          return {
            position: {
              x: rect.left,
              y: rect.top,
              width: rect.width,
              height: rect.height,
            },
            keyName: key,
            keyNamespace: el.getAttribute(DATA_I18N_NS) ?? '',
          };
        });
      },

      outputNotFormattable: false,
    });
  };
}

/**
 * I18n 收集器插件
 *
 * 在 VITE_SOURCE_LOCATOR 模式下启用，用于收集页面上所有的 i18n 翻译调用。
 *
 */
export const I18nCollectorPlugin = (): TolgeePlugin => (tolgee, tools) => {
  console.log('import.meta.env.VITE_SOURCE_LOCATOR', import.meta.env.VITE_SOURCE_LOCATOR);
  if (
    import.meta.env.VITE_SOURCE_LOCATOR === 'true' ||
    import.meta.env.VITE_SOURCE_LOCATOR === '1'
  ) {
    // 设置自定义 Observer
    tools.setObserver(createCollectorObserver());
  }

  return tolgee;
};
